import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/sleek";

const SLOTS = [
  { key: "hero", label: "Hero (top banner)" },
  { key: "offer_cabinets", label: "Cabinets & Countertops card" },
  { key: "offer_renovation", label: "Full Renovation card" },
];

const COPY_FIELDS = [
  { key: "hero_headline_1", label: "Hero headline — line 1", max: 80 },
  { key: "hero_headline_2", label: "Hero headline — line 2", max: 80 },
  { key: "hero_sub", label: "Hero subtext", max: 500, area: true },
  { key: "cab_title", label: "Cabinets card — title", max: 60 },
  { key: "cab_desc", label: "Cabinets card — description", max: 600, area: true },
  { key: "ren_title", label: "Renovation card — title", max: 60 },
  { key: "ren_desc", label: "Renovation card — description", max: 600, area: true },
];

export const LandingManager = ({ token }) => {
  const api = useMemo(() => adminApi(token), [token]);
  const [cfg, setCfg] = useState(null);
  const [library, setLibrary] = useState([]);
  const [picker, setPicker] = useState(null); // {slot} or {slot:'gallery', index}
  const [folder, setFolder] = useState("All");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/landing-config").then((r) => setCfg(r.data)).catch(() => toast.error("Could not load landing config."));
    api.get("/admin/photo-library").then((r) => setLibrary(r.data.photos)).catch(() => toast.error("Could not load photo library."));
  }, [api]);

  const folders = useMemo(() => ["All", ...Array.from(new Set(library.map((p) => p.folder)))], [library]);
  const visible = useMemo(() => (folder === "All" ? library : library.filter((p) => p.folder === folder)), [library, folder]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/landing-config", next);
      setCfg(data);
      toast.success("Landing page updated.");
    } catch {
      toast.error("Save failed — please try again.");
    } finally { setSaving(false); }
  };

  const choose = (path) => {
    const next = { ...cfg };
    if (picker.slot === "gallery") {
      next.gallery = [...cfg.gallery];
      next.gallery[picker.index] = path;
    } else {
      next[picker.slot] = path;
    }
    setPicker(null);
    save(next);
  };

  if (!cfg) return <div className="mt-8 text-sm text-zinc-500" data-testid="landing-manager-loading">Loading…</div>;

  const Slot = ({ label, src, onClick, testid }) => (
    <button onClick={onClick} className="text-left group" data-testid={testid}>
      <div className="aspect-[4/3] overflow-hidden border border-zinc-200 relative">
        <img src={src} alt={label} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 text-white text-xs tracking-[0.2em] uppercase transition-opacity">Change photo</span>
        </div>
      </div>
      <div className="text-xs text-zinc-600 mt-2">{label}</div>
    </button>
  );

  return (
    <div className="mt-8" data-testid="landing-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-light">Landing page photos <span className="text-zinc-400 text-sm">(/kitchens)</span></h2>
        {saving && <span className="text-xs text-zinc-500">Saving…</span>}
      </div>
      <p className="text-sm text-zinc-500 mt-2">Click any photo to swap it. Changes appear on the landing page immediately (redeploy to update the live site).</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-6">
        {SLOTS.map((s) => (
          <Slot key={s.key} label={s.label} src={cfg[s.key]} onClick={() => setPicker({ slot: s.key })} testid={`landing-slot-${s.key}`} />
        ))}
      </div>

      <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-10">Gallery (6 photos)</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-4">
        {cfg.gallery.map((g, i) => (
          <Slot key={`${g}-${i}`} label={`Gallery ${i + 1}`} src={g} onClick={() => setPicker({ slot: "gallery", index: i })} testid={`landing-slot-gallery-${i}`} />
        ))}
      </div>

      <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-12">Headline &amp; offer text</div>
      <div className="mt-4 border border-zinc-200 p-6 md:p-8 grid md:grid-cols-2 gap-x-8 gap-y-5" data-testid="landing-copy-editor">
        {COPY_FIELDS.map((f) => (
          <div key={f.key} className={f.area ? "md:col-span-2" : ""}>
            <label className="text-xs text-zinc-500 flex justify-between">
              <span>{f.label}</span>
              <span className="text-zinc-400">{(cfg[f.key] || "").length}/{f.max}</span>
            </label>
            {f.area ? (
              <textarea
                value={cfg[f.key] || ""}
                maxLength={f.max}
                onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
                className="mt-1.5 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-950 min-h-[80px]"
                data-testid={`landing-copy-${f.key}`}
              />
            ) : (
              <input
                value={cfg[f.key] || ""}
                maxLength={f.max}
                onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
                className="mt-1.5 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-950"
                data-testid={`landing-copy-${f.key}`}
              />
            )}
          </div>
        ))}
        <div className="md:col-span-2">
          <button
            onClick={() => save(cfg)}
            disabled={saving}
            className="bg-zinc-950 text-white px-6 py-3 text-sm hover:bg-[#FF001E] transition-colors disabled:opacity-60"
            data-testid="landing-copy-save"
          >
            {saving ? "Saving…" : "Save copy"}
          </button>
        </div>
      </div>

      {picker && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPicker(null)} data-testid="landing-photo-picker">
          <div className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
              <div className="text-sm font-medium">Choose a photo</div>
              <div className="flex items-center gap-3">
                <select value={folder} onChange={(e) => setFolder(e.target.value)} className="border border-zinc-300 text-xs px-2 py-1.5" data-testid="landing-picker-folder">
                  {folders.map((f) => <option key={f}>{f}</option>)}
                </select>
                <button onClick={() => setPicker(null)} data-testid="landing-picker-close"><X size={18} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-5 grid grid-cols-3 md:grid-cols-5 gap-3">
              {visible.map((p) => (
                <button key={p.path} onClick={() => choose(p.path)} className="aspect-square overflow-hidden border border-zinc-200 hover:border-[#FF001E] transition-colors" data-testid="landing-picker-photo">
                  <img src={p.path} alt={p.path.split("/").pop()} loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingManager;
